import { useState } from "react";
import axios from "axios";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Loader2, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function validateField(name, value) {
  switch (name) {
    case "name":
      if (!value.trim()) return "Tu nombre es obligatorio.";
      if (value.trim().length < 2) return "Mínimo 2 caracteres.";
      return "";
    case "email":
      if (!value.trim()) return "Tu email es obligatorio.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Formato de email inválido.";
      return "";
    default:
      return "";
  }
}

const inputCls = (hasError) =>
  `h-12 text-base bg-white ${hasError ? "border-red-400 focus:ring-red-300/40" : ""}`;

export default function LeadMagnetForm({ utm, onSuccess }) {
  const [openedAt] = useState(() => Date.now());
  const [form, setForm] = useState({ name: "", email: "", honeypot: "", consent: true });
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (touched[k]) {
      setFieldErrors((fe) => ({ ...fe, [k]: validateField(k, val) }));
    }
  };

  const blur = (k) => () => {
    setTouched((t) => ({ ...t, [k]: true }));
    setFieldErrors((fe) => ({ ...fe, [k]: validateField(k, form[k]) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    const checks = ["name", "email"];
    const newErrors = {};
    checks.forEach((k) => { newErrors[k] = validateField(k, form[k]); });
    setFieldErrors(newErrors);
    setTouched({ name: true, email: true });

    if (Object.values(newErrors).some(Boolean)) return;
    if (!form.consent) {
      setError("Debes aceptar recibir el recurso para continuar.");
      return;
    }

    setSubmitting(true);
    try {
      const now = Date.now();
      const elapsed_ms = now - openedAt;
      const jsToken = btoa(`${now}|${Math.floor(now / 1000) % 4999}`);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        utm_source: utm?.utm_source || null,
        utm_medium: utm?.utm_medium || null,
        utm_campaign: utm?.utm_campaign || null,
        gclid: utm?.gclid || null,
        elapsed_ms,
        js_token: jsToken,
        honeypot: form.honeypot,
      };
      await axios.post(`${API}/lead-magnet`, payload);
      onSuccess?.({ name: form.name.trim(), email: form.email.trim() });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo procesar tu solicitud. Inténtalo de nuevo.";
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-magnet-form" noValidate>
      {/* honeypot */}
      <input
        type="text"
        name="website"
        value={form.honeypot}
        onChange={set("honeypot")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-[12px] font-bold tracking-wider uppercase text-slate-300">
            Nombre <span className="text-[#F26522]">*</span>
          </Label>
          <Input
            data-testid="lead-magnet-name"
            value={form.name}
            onChange={set("name")}
            onBlur={blur("name")}
            placeholder="Tu nombre"
            maxLength={80}
            className={inputCls(fieldErrors.name)}
          />
          {fieldErrors.name && <p className="text-xs text-red-400 font-medium">{fieldErrors.name}</p>}
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px] font-bold tracking-wider uppercase text-slate-300">
            Email <span className="text-[#F26522]">*</span>
          </Label>
          <Input
            data-testid="lead-magnet-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            onBlur={blur("email")}
            placeholder="nombre@empresa.com"
            maxLength={100}
            className={inputCls(fieldErrors.email)}
          />
          {fieldErrors.email && <p className="text-xs text-red-400 font-medium">{fieldErrors.email}</p>}
        </div>
      </div>

      <div className="flex items-start gap-2.5 pt-1">
        <Checkbox
          id="lead-magnet-consent"
          data-testid="lead-magnet-consent"
          checked={form.consent}
          onCheckedChange={(v) => setForm((f) => ({ ...f, consent: Boolean(v) }))}
          className="mt-0.5 border-slate-400 data-[state=checked]:bg-[#F26522] data-[state=checked]:border-[#F26522]"
        />
        <Label htmlFor="lead-magnet-consent" className="text-xs text-slate-400 leading-relaxed font-normal cursor-pointer">
          Acepto recibir el Capítulo 1 de Tracción por email
        </Label>
      </div>

      {error && (
        <p data-testid="lead-magnet-error" className="text-sm text-red-400 font-medium">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-magnet-submit"
        className="btn-primary w-full text-base disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Enviando…
          </>
        ) : (
          <>
            Descargar Capítulo 1 gratis
            <Download className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
